import { logger } from 'src/analytics/KatalLogger';
import { retrieveParameterFromSSM } from 'src/utils/ssm-parameter';
import { FeatureFlagParameters, FeatureFlagStatus } from './types/featureFlags';
import ENVIRONMENT_VARIABLES from 'src/constants/environment-variables';

/** Feature flags that default to enabled in dev/alpha stages */
const DEV_ENABLED_FLAGS: Set<FeatureFlagParameters> = new Set([
  FeatureFlagParameters.ACCESS_REQUEST_SELF_SERVICE
]);

/**
 * Validates the feature flag status string and returns the corresponding FeatureFlagStatus
 * @param status - The status string to validate
 * @returns FeatureFlagStatus - Returns ENABLED if valid and matches, otherwise returns DISABLED
 */
const validateFeatureFlagStatus = (status: string): FeatureFlagStatus =>
  Object.values(FeatureFlagStatus).includes(status as FeatureFlagStatus) ? (status as FeatureFlagStatus) : FeatureFlagStatus.DISABLED;

/**
 * Checks if a feature flag is enabled by retrieving its status from SSM Parameter Store
 * @param parameter - The SSM parameter name for the feature flag
 * @returns Promise<boolean> - Returns true if the feature is enabled, false otherwise
 */
export const isFeatureEnabled = async (parameter: FeatureFlagParameters): Promise<boolean> => {
  if (!parameter) {
    logger.error('Feature flag parameter cannot be empty or null');
    return false;
  }

  try {
    const result = await retrieveParameterFromSSM(parameter);
    return validateFeatureFlagStatus(result) === FeatureFlagStatus.ENABLED;
  } catch (error) {
    // In dev/alpha, default to enabled for specified flags when SSM is unavailable
    const stage = ENVIRONMENT_VARIABLES.env.Stage;
    if ((stage === 'dev' || stage === 'alpha') && DEV_ENABLED_FLAGS.has(parameter)) {
      logger.warn(`Feature flag ${parameter} defaulting to enabled (SSM unavailable in ${stage})`);
      return true;
    }
    logger.error(`Error checking feature flag ${parameter}: ${error}`);
    return false;
  }
};
